import styled, { keyframes, css, SimpleInterpolation } from 'styled-components';
import {
  AnnotationButtonSize,
  AnnotationButtonStyle,
  AnnotationButtonLayoutType,
  AnnotationFontSize
} from '@fable/common/dist/types';
import { Rect } from '../base/hightligher-base';
import { generateShadeColor } from './utils';
import { getColorContrast } from '../../utils';

export const AnContent = styled.div<{
  bgColor: string,
  borderRadius: number,
  primaryColor: string,
  fontColor: string,
  padding: [number, number]
}>`
  --f-ann-bg-color: ${(p) => p.bgColor};
  --f-ann-border-radius: ${(p) => p.borderRadius}px;
  --f-ann-primary-color: ${(p) => p.primaryColor};
  --f-ann-font-color: ${(p) => p.fontColor};
  --f-ann-padding-y: ${(p) => p.padding[0]};
  --f-ann-padding-x: ${(p) => p.padding[1]};

  --f-font-normal: 18px;
  --f-font-large: 24px;
  --f-font-huge: 30px;

  @media screen and (max-width: 389px) {
    --f-font-normal: 14px;
    --f-font-large: 18px;
    --f-font-huge: 22px;
  }

  background: ${(p) => `linear-gradient(45deg, color-mix(in srgb, ${p.bgColor} 97%, white) 0%, color-mix(in srgb, ${p.bgColor} 97%, black) 100%)`};
  font-size: 1.1rem;
  position: absolute;
  border-radius: 4px;
  padding: 0;
  margin: 0;

  a {
    color: inherit;
    text-decoration: underline dotted;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .LeadForm__container {
    display: block;
    cursor: pointer;
    user-select: none;
    font-family: inherit;
    color: inherit;
    font-size: inherit;
    border-radius: var(--f-ann-border-radius);
    background-color: ${p => (getColorContrast(p.bgColor) === 'dark' ? '#ffffff1a' : '#bcbcbc1a')};
    border: none;
    margin-top: 0;
    padding: 0 calc(var(--f-ann-padding-x) * 0.75px) 1.5rem
  }

  .LeadForm__container.focused {
    outline: 2px solid var(--f-ann-primary-color);
  }

  .LeadForm__inner {
    width: 100%;
    cursor: default;
    display: flex;
    flex-direction: column;
  }

  .LeadForm__optionContainer {
    display: flex;
    flex-direction: column-reverse;
    align-items: center;
    margin: 0px;
  }

  .LeadForm__inputValidation {
    transform: translate(1rem, 1rem);
    background: color-mix(in srgb, var(--f-ann-bg-color) 95%, white);
    display: inline-block;
    width: auto;
    align-self: baseline;
    color: color-mix(in srgb, var(--f-ann-font-color) 90%, white);
    filter: hue-rotate(180deg);
    padding: 2px 7px;
    border-radius: 4px;
    font-size: 14px;
    visibility: hidden;
    margin-bottom: 6px;
  }

  .LeadForm__optionInputWrapper {
    flex: 1;
    display: flex;
    width: 100%;
    border: none;
    box-shadow: 0 0 0 2px var(--f-ann-primary-color);
    padding: 4px 0;
    border-radius: 5px;
    position: relative;
    overflow: hidden;
    cursor: pointer;
  }

  .LeadForm__optionInputInAnn {
    width: 100%;
    font-size: inherit;
    display: flex;
    flex: 1px;
    font-size: 1.35rem;
    border: 0px;
    padding: 8px 16px;
    color: var(--fable-ann-font-color);
    background-color: transparent !important;
    font-weight: bold;
    outline: 0px;
    z-index: 0;
  }

  .LeadForm__optionInputInAnn:focus {
    background-color: transparent !important;
  }
  
  .LeadForm__optionInputInAnn::placeholder {
    font-weight: normal !important;
    color: var(--f-ann-font-color) !important;
    opacity: 0.75 !important;
  }

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  textarea:-webkit-autofill,
  textarea:-webkit-autofill:hover,
  textarea:-webkit-autofill:focus,
  select:-webkit-autofill,
  select:-webkit-autofill:hover,
  select:-webkit-autofill:focus {
    -webkit-text-fill-color: ${(p) => p.fontColor};
    -webkit-box-shadow: 0 0 0px 1000px transparent inset;
    transition: background-color 5000s ease-in-out 0s;
    background-color: transparent;
  }
`;

const getAnnotationPadding = (annotationPadding: string): string => {
  const formatRegex = /^\s*(\d+)\s*(\d*)\s*$/;
  const formatedPadding = annotationPadding.match(formatRegex);
  if (!formatedPadding) {
    return '14px 14px';
  }

  const verticalPadding = parseInt(formatedPadding[1], 10);
  const horizontalPadding = parseInt(formatedPadding[2], 10);
  if (Number.isNaN(horizontalPadding)) {
    return `${verticalPadding}px ${verticalPadding}px`;
  }

  if (Number.isNaN(verticalPadding)) {
    return '14px 14px';
  }

  return `${verticalPadding}px ${horizontalPadding}px`;
};

const getVerticalPadding = (annotationPadding: string) : string => {
  const formatedPadding = getAnnotationPadding(annotationPadding);
  const verticalPadding = formatedPadding.split(/\s/)[0];
  return verticalPadding;
};

interface AnInnerConProps {
  anPadding: string;
}

export const AnInnerContainer = styled.div`
  display:flex;
  flex-direction:column;
  padding: ${(props: AnInnerConProps) => `${getAnnotationPadding(props.anPadding)}`};
  z-index: 9999;
`;

interface AnTextContentProps {
  fontFamily: string | null;
  fontColor: string | null;
  borderRadius: number;
}

export const AnTextContent = styled.div`
  font-weight: 500;

  p {
    margin: 0;
    font-weight: 500;
  }
  p img {
    margin-top: 0.2em;
    border-radius: ${(props: AnTextContentProps) => `${props.borderRadius}px`};
  }

  p {
    font-size: ${AnnotationFontSize.normal};
    font-family: ${(p: AnTextContentProps) => p.fontFamily || 'inherit'};
    color: ${(p: AnTextContentProps) => `${p.fontColor}`};
  }
`;

interface ButtonConProps {
  justifyContent: string;
  borderTopColor: string;
  btnLength: number;
  flexDirection: 'row' | 'column';
  anPadding: string;
}

export const ButtonCon = styled.div`
  position: relative;
  display: flex;
  justify-content: ${(props: ButtonConProps) => props.justifyContent};
  align-items: center;
  margin-top: ${(props: ButtonConProps) => `calc(0.25rem + ${getVerticalPadding(props.anPadding)})`};
  padding-top: 1rem;
  border-top: 1px solid ${(props: ButtonConProps) => props.borderTopColor};
  row-gap: 0.5rem;
  flex-direction: ${(props: ButtonConProps) => props.flexDirection};

  ${(props: ButtonConProps) => (
    props.btnLength !== 2 || props.flexDirection === 'column'
      ? `& > p {
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%) translateY(-50%); 
        padding: 0 10px;
      }`
      : ''
  )}
`;

export const Progress = styled.p<{bg: string; fg: string, fontFamily: string}>`
  font-size: 1rem;
  margin: 0;
  backdrop-filter: blur(8px);
  background-color: #0000ff00;
  color: ${props => props.fg}a8;
  font-family: ${props => props.fontFamily};
`;

export const ABtn = styled.button`
  border-radius: 2px;
  font-size: 18px;
  font-weight: bold;

  border-radius: ${(props: BtnConf) => `${props.borderRadius}px`};

  order: ${(props: BtnConf) => (props.idx === 0 ? -1 : 0)};

  border: ${(p: BtnConf) => {
    if (p.btnStyle === AnnotationButtonStyle.Outline) return `1px solid ${p.color}`;
    return 'none';
  }};

  background: ${(p: BtnConf) => {
    if (p.btnStyle === AnnotationButtonStyle.Primary) return p.color;
    return '#ffffff00';
  }};
  
  color: ${(p: BtnConf) => {
    if (p.btnStyle === AnnotationButtonStyle.Primary) return getColorContrast(p.color) === 'dark' ? '#fff' : '#000';
    return getColorContrast(p.bg) === 'dark' ? '#fff' : '#000';
  }};

  padding: ${(p: BtnConf) => {
    if (p.size === AnnotationButtonSize.Large) {
      return p.noPad ? '12px 0px' : '12px 22px';
    } if (p.size === AnnotationButtonSize.Medium) {
      return p.noPad ? '8px 0px' : '8px 18px';
    }
    return p.noPad ? '4px 0px' : '4px 12px';
  }};
  font-family: ${(p: BtnConf) => p.fontFamily || 'inherit'};
  &:hover {
    cursor: pointer;
    text-decoration: ${(p: BtnConf) => (p.btnStyle === 'link' ? 'underline' : 'none')};
  }
  width:  ${(p: BtnConf) => (p.btnLayout === 'default' ? 'auto' : '100%')};
  opacity: 0.8;
  transition: opacity 0.2s ease-out;

  &:hover {
    opacity: 1;
  }
`;

export interface BtnConf {
  btnStyle: AnnotationButtonStyle;
  size: AnnotationButtonSize;
  color: string;
  fontFamily: string | null;
  btnLayout: AnnotationButtonLayoutType;
  borderRadius: number;
  idx?: number;
  bg: string;
  noPad?: boolean;
}

interface AnHotspotProps {
  selColor: string;
  box: Rect;
  scrollX: number;
  scrollY: number;
  shouldAnimate: boolean;
}

const createBoxShadowKF = (selColor: string) : SimpleInterpolation => keyframes`
  50% {box-shadow: 0 0 0 4px ${selColor};}
`;

const BoxShadowKFRule = css`
  ${({ selColor }: { selColor: string }) => css`
    ${createBoxShadowKF(selColor)} 2s infinite;
  `}
`;

export const AnHotspot = styled.div`
  background: transparent;
  cursor: pointer;
  animation: ${(p: AnHotspotProps) => (p.shouldAnimate ? BoxShadowKFRule : 'none')};
  border-radius: 4px;
  position: absolute;
  top: ${(p: AnHotspotProps) => `${p.box.top}px`};
  left: ${(p: AnHotspotProps) => `${p.box.left}px`};
  width: ${(p: AnHotspotProps) => `${p.box.width}px`};
  height: ${(p: AnHotspotProps) => `${p.box.height}px`};
  transition: all 0.3s ease-in-out;

  &:hover {
    background-color: color-mix(in srgb, ${(p: AnHotspotProps) => p.selColor} 10%, rgba(0, 0, 0, 0.1));
  }
`;

export const AnBubble = styled.div<{bubbleWidth: number}>`
  width: ${(p) => `${p.bubbleWidth}px`}; 
  height: ${(p) => `${p.bubbleWidth}px`}; 
  cursor: pointer;
`;

export const AnVideoContainer = styled.div`
  border-radius: 8px;
`;

export const AnVideo = styled.video<{ border: string }>`
  width: 100%;
  border-radius: 8px;
  box-shadow: ${p => `${p.border}`};
`;

export const AnVideoControls = styled.div<{showOverlay: boolean}>`
  transition: all 0.2s ease-in-out;
  background-color: ${(p) => (p.showOverlay ? 'rgba(0, 0, 0, 0.4)' : 'transparent')};
  height: 100%;
  border-radius: 8px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
`;

export const AnVideoCtrlBtn = styled.button<{ showButton: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  color: white;
  background-color: transparent;
  font-size: 2rem;
  border: none;
  display: ${p => (p.showButton ? 'flex' : 'none')};
  cursor: pointer;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;

  > span.anticon {
    display: block;
    height: 1em;
  }

  transition: all 0.2s ease-in-out;

  &:hover {
    transform: translate(-50%, -50%) scale(1.2);
    background-color: rgba(255, 255, 255, 0.25);
    border-radius: 50%;
  }
`;

export const ReplayButton = styled.button<{ pcolor: string }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  height: 56px;
  width: 56px;
  border-radius: 50%;
  background: ${p => generateShadeColor(p.pcolor, 200)};
  color: ${p => `${p.pcolor}`};
  font-size: 1.6rem;
  border: none;
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease-in-out;

  &:hover {
    transform: translate(-50%, -50%) scale(1.2);
  }

  > span.anticon {
    display: block;
    height: 1em;
  }
`;

const bottomToTop = keyframes`
  from {
    bottom: -1rem;
    opacity: 0;
  }

  to {
    bottom: 0.5rem;
    opacity: 1;
  }
`;

export const NavButtonCon = styled.div<{ pcolor: string }>`
  position: absolute;
  display: flex;
  justify-content: space-between;
  align-items: center;
  bottom: 0.75rem;
  left: 0.75rem;
  right: 0.75rem;

  animation: ${bottomToTop} 0.2s ease-in-out;

  .serial-num {
    color: ${p => generateShadeColor(p.pcolor, -200)};
  }

  .next-btn {
    color: ${p => generateShadeColor(p.pcolor, 200)};
    background: ${p => `${p.pcolor}bf`};

    &:hover {
      background: ${p => `${p.pcolor}`};
    }
  }

  .back-btn {
    background: ${p => generateShadeColor(p.pcolor, 200)};
    opacity: 0.8;
    color: ${p => `${p.pcolor}`};

    &:hover {
      opacity: 1;
    }
  }
`;

const helpBubblePulse = keyframes`
  0% {
    transform: scale(1);
    opacity: .75;
  }
  25% {
    transform:scale(1);
    opacity:.75;
  }
  100% {
    transform:scale(2.5);
    opacity:0
  }
`;

export const HelpBubble = styled.a<{selColor?: string, bubbleDiameter: number}>`
  display: block;
  position: absolute;
  z-index: 2;
  cursor: pointer;
  left: 0px;
  top: 0px;

  &:after {
    content: "";
    background-color: ${({ selColor }) => selColor || 'rgba(117,103,255)'};
    width: ${({ bubbleDiameter }) => bubbleDiameter}px;
    height: ${({ bubbleDiameter }) => bubbleDiameter}px;
    border-radius: 50%;
    position: absolute;
    display: block;
    top: 1px;
    left: 1px;
  }
`;

export const HelpBubbleOuterDot = styled.span<{ selColor?: string, bubbleDiameter: number }>`
  margin: 1px;
  display: block;
  text-align: center;
  opacity: 1;
  background-color: ${({ selColor }) => selColor || 'rgba(117,103,255)'};
  width: ${({ bubbleDiameter }) => bubbleDiameter}px;
  height: ${({ bubbleDiameter }) => bubbleDiameter}px;
  border-radius: 50%;
  animation: ${helpBubblePulse} 1.5s linear infinite;
`;

export const HelpBubbleInnerDot = styled.span<{ selColor?: string, bubbleDiameter: number }>`
  background-position: absolute;
  display: block;
  text-align: center;
  opacity: 1;
  background-color: ${({ selColor }) => selColor || 'rgba(117,103,255)'};
  width: ${({ bubbleDiameter }) => bubbleDiameter}px;
  height: ${({ bubbleDiameter }) => bubbleDiameter}px;
  border-radius: 50%;
  -webkit-animation: ${helpBubblePulse} 1.5s linear infinite;
  -moz-animation: ${helpBubblePulse} 1.5s linear infinite;
  -o-animation: ${helpBubblePulse} 1.5s linear infinite;
  animation: ${helpBubblePulse} 1.5s linear infinite;

  &:after {
  content: "";
  background-position: absolute;
  display: block;
  text-align: center;
  opacity: 1;
  background-color: ${({ selColor }) => selColor || 'rgba(117,103,255, 0.5)'};
  width: ${({ bubbleDiameter }) => bubbleDiameter}px;
  height: ${({ bubbleDiameter }) => bubbleDiameter}px;
  border-radius: 50%;
  -webkit-animation: ${helpBubblePulse} 1.5s linear infinite;
  -moz-animation: ${helpBubblePulse} 1.5s linear infinite;
  -o-animation: ${helpBubblePulse} 1.5s linear infinite;
  animation: ${helpBubblePulse} 1.5s linear infinite;
  }
`;

export const Con = styled.div`
  position: fixed;
`;
